/**
 * LanguageSelector Component
 *
 * Dropdown component for selecting the application language.
 * Displays current language with flag and name, with expandable options.
 *
 * Features:
 * - Keyboard navigable (Tab, Enter, Escape, Arrow keys)
 * - ARIA-compliant with proper labels and roles
 * - Immediate language switching on selection
 * - Mobile-friendly touch targets (44x44px minimum)
 * - Dark mode support
 *
 * @module components/common/LanguageSelector
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage, getCurrentLanguage } from '../../i18n';

/**
 * Supported languages configuration
 */
const SUPPORTED_LANGUAGES = [
  { code: 'fr' as const, name: 'Français', flag: '🇫🇷' },
  { code: 'en' as const, name: 'English', flag: '🇬🇧' },
];

type LanguageCode = 'fr' | 'en';

/**
 * LanguageSelector Props
 */
export interface LanguageSelectorProps {
  /** Additional CSS classes */
  className?: string;
  /** Variant for different display contexts */
  variant?: 'header' | 'compact';
}

/**
 * LanguageSelector Component
 *
 * Displays the current language with a dropdown to switch between available languages.
 * Supports keyboard navigation and is accessible via screen readers.
 */
export function LanguageSelector({
  className = '',
  variant = 'header',
}: LanguageSelectorProps): JSX.Element {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  // Get current language config (guaranteed to exist)
  const currentLangConfig = SUPPORTED_LANGUAGES.find(
    (lang) => lang.code === currentLanguage
  ) ?? { code: 'fr' as const, name: 'Français', flag: '🇫🇷' };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          setIsOpen(false);
          setFocusedIndex(-1);
          buttonRef.current?.focus();
          event.preventDefault();
          break;

        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) =>
              prev < SUPPORTED_LANGUAGES.length - 1 ? prev + 1 : 0
            );
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(SUPPORTED_LANGUAGES.length - 1);
          } else {
            setFocusedIndex((prev) =>
              prev > 0 ? prev - 1 : SUPPORTED_LANGUAGES.length - 1
            );
          }
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(0);
          } else if (focusedIndex >= 0 && focusedIndex < SUPPORTED_LANGUAGES.length) {
            const selectedLang = SUPPORTED_LANGUAGES[focusedIndex];
            if (selectedLang) {
              handleLanguageSelect(selectedLang.code);
            }
          }
          break;

        case 'Tab':
          if (isOpen) {
            setIsOpen(false);
            setFocusedIndex(-1);
          }
          break;

        case 'Home':
          if (isOpen) {
            event.preventDefault();
            setFocusedIndex(0);
          }
          break;

        case 'End':
          if (isOpen) {
            event.preventDefault();
            setFocusedIndex(SUPPORTED_LANGUAGES.length - 1);
          }
          break;
      }
    },
    [isOpen, focusedIndex]
  );

  // Handle language selection
  const handleLanguageSelect = useCallback(
    async (locale: LanguageCode) => {
      await changeLanguage(locale);
      setIsOpen(false);
      setFocusedIndex(-1);
      buttonRef.current?.focus();
    },
    []
  );

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      // Find current language index for initial focus
      const currentIndex = SUPPORTED_LANGUAGES.findIndex(
        (lang) => lang.code === currentLanguage
      );
      setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
    } else {
      setFocusedIndex(-1);
    }
  }, [isOpen, currentLanguage]);

  // Focus the focused item when dropdown opens
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listboxRef.current) {
      const items = listboxRef.current.querySelectorAll('[role="option"]');
      (items[focusedIndex] as HTMLElement)?.focus();
    }
  }, [isOpen, focusedIndex]);

  // Styles based on variant
  const isCompact = variant === 'compact';

  const buttonClasses = isCompact
    ? `flex items-center gap-1 sm:gap-2 min-h-[36px] sm:min-h-[44px] min-w-[36px] sm:min-w-[44px] px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors ${className}`
    : `flex items-center gap-2 min-h-[44px] px-3 py-2 rounded-lg text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors ${className}`;

  const dropdownClasses = 'absolute right-0 mt-1 py-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50 min-w-[160px]';

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className={buttonClasses}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('language.selector', 'Sélecteur de langue')}
        aria-controls="language-listbox"
      >
        <span className="text-sm sm:text-base" aria-hidden="true">
          {currentLangConfig.flag}
        </span>
        <span className={isCompact ? 'hidden sm:inline text-xs sm:text-sm font-medium' : 'text-sm font-medium'}>
          {currentLangConfig.name}
        </span>
        {/* Chevron icon */}
        <svg
          className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <ul
          ref={listboxRef}
          id="language-listbox"
          role="listbox"
          aria-label={t('language.selector', 'Sélecteur de langue')}
          aria-activedescendant={
            focusedIndex >= 0 && focusedIndex < SUPPORTED_LANGUAGES.length
              ? `language-option-${SUPPORTED_LANGUAGES[focusedIndex]?.code ?? ''}`
              : undefined
          }
          className={dropdownClasses}
        >
          {SUPPORTED_LANGUAGES.map((lang, index) => {
            const isSelected = lang.code === currentLanguage;
            const isFocused = index === focusedIndex;

            return (
              <li
                key={lang.code}
                id={`language-option-${lang.code}`}
                role="option"
                aria-selected={isSelected}
                tabIndex={isFocused ? 0 : -1}
                onClick={() => handleLanguageSelect(lang.code)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={`flex items-center gap-3 px-4 py-3 min-h-[44px] cursor-pointer transition-colors
                  ${isFocused ? 'bg-violet-50 dark:bg-violet-900/30' : ''}
                  ${isSelected ? 'text-violet-700 dark:text-violet-300 font-medium' : 'text-slate-700 dark:text-slate-300'}
                  hover:bg-violet-50 dark:hover:bg-violet-900/30`}
              >
                <span className="text-base" aria-hidden="true">
                  {lang.flag}
                </span>
                <span className="flex-1 text-sm">{lang.name}</span>
                {isSelected && (
                  <svg
                    className="w-5 h-5 text-violet-600 dark:text-violet-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default LanguageSelector;
